import React, { useEffect, useState, useRef } from "react";
import { Alert } from "antd";
import MainWrapper from "../../components/MainWrapper";
import SpinerIndicator from "../../components/SpinerIndicator";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { useParams } from "react-router-dom";
import { transactionApi } from "../../config/axiosConfig";
import SuccessMessage from "../../components/SuccessMessage";

const VNDqrCodeInfo = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, error, data: verifyTranData } = useSelector(
    (state) => state.getVerifyManualTransactionReducer
  );

  const postQrInitManualState = useSelector(
    (state) => state.postQrInitManualReducer
  );

  const {
    data: qrInitData,
    error: qrInitError,
    isLoading: qrInitLoading,
  } = postQrInitManualState;

  const qrImage =
    qrInitData?.qrUrl ||
    qrInitData?.qrCode 

  /* ⏱️ QR Expiry Timer (5 min) */
  const [timeLeft, setTimeLeft] = useState(300);

  /* 🔁 Polling ref */
  const pollingTimeoutRef = useRef(null);

  /* ❌ QR Status Error */
  const [qrStatusError, setQrStatusError] = useState("");

  /* 💳 Payment Status */
  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  // PENDING | PAID | REJECTED

  /* -------------------- QR TIMER -------------------- */
  useEffect(() => {
    if (!qrInitData) return;

    setTimeLeft(300);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [qrInitData]);

  const formatTime = (seconds) => {
    const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
    const ss = String(seconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  /* -------------------- QR STATUS POLLING -------------------- */
  useEffect(() => {
    if (!verifyTranData?.trxRef || !verifyTranData?.merchantId) return;

    let isCancelled = false;

    const checkQrStatus = async () => {
      try {
        const res = await transactionApi.get(
          `vnd/transactionvnd/qr-vnd-check/${verifyTranData.trxRef}/${verifyTranData.merchantId}`
        );

        const status = res?.data?.data?.status;

        /* 00 / 01 → keep polling */
        if (status === "00_Initiated" || status === "01_Pending") {
          if (!isCancelled) {
            pollingTimeoutRef.current = setTimeout(checkQrStatus, 5000);
          }
          return;
        }

        /* 02 → PAID */
        if (status === "02_Paid") {
          setPaymentStatus("PAID");
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
          return;
        }

        /* 03 → REJECTED */
        if (status === "03_Rejected") {
          setPaymentStatus("REJECTED");
          setQrStatusError("Payment was rejected.");
          if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
          }
        }
      } catch (err) {
        setQrStatusError(
          err?.response?.data?.message ||
            "QR code has expired or is no longer valid."
        );

        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
        }
      }
    };

    checkQrStatus();

    return () => {
      isCancelled = true;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, [verifyTranData]);

  useEffect(() => {
    if (!verifyTranData) {
      navigate(`/vnd/${params.id}`)
    }
  }, [])

  // --------------- REDIRECT AFTER PAYMENT SUCCESS (5 sec) ---------------
  useEffect(() => {
    if (paymentStatus !== "PAID") return;

    const getRedirectionUrl = localStorage.getItem("redirectionUrl");

    const timer = setTimeout(() => {
      if (getRedirectionUrl) {
        window.location.replace(getRedirectionUrl);
        localStorage.removeItem("redirectionUrl");
      } else if (verifyTranData?.redirectUrl) {
        window.location.replace(verifyTranData.redirectUrl);
      } else if (verifyTranData?.siteUrl) {
        window.location.replace(verifyTranData.siteUrl);
      }
    }, 5000); // ⏱️ 5 seconds delay

    return () => clearTimeout(timer);
  }, [paymentStatus, verifyTranData]);

  /* ❌ Final QR Invalid State */
  const isQrInvalid = timeLeft === 0 || paymentStatus === "REJECTED";

  return (
    <div style={{ minHeight: "100dvh" }}>
      <MainWrapper>
        {(isLoading || qrInitLoading) && <SpinerIndicator />}

        {(error || qrInitError) && (
          <Alert
            type="error"
            showIcon
            message="Something went wrong"
            description={error || qrInitError}
          />
        )}

        {/* ✅ PAYMENT SUCCESS */}
        {paymentStatus === "PAID" && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
            <SuccessMessage width={"100%"} height={"40%"} />
            <Alert
              type="success"
              showIcon
              message="Payment Successful"
              description="Your payment has been received successfully."
            />
            {verifyTranData?.trxRef && (
              <div
                style={{
                  background: "#f5f7fa",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                <b>Reference:</b> {verifyTranData.trxRef}
              </div>
            )}
          </div>
        )}

        {/* ❌ QR EXPIRED / REJECTED */}
        {isQrInvalid && paymentStatus !== "PAID" && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 40 }}>
            <div
              style={{
                background: "#fff",
                padding: 32,
                borderRadius: 16,
                boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                maxWidth: 420,
                width: "100%",
                textAlign: "center",
              }}
            >
              <h3 style={{ color: "#cf1322", marginBottom: 12 }}>
                QR Code Expired
              </h3>

              <p style={{ color: "#666", marginBottom: 20 }}>
                {qrStatusError ||
                  "This QR code has expired. Please regenerate a new QR code to continue payment."}
              </p>

              <Alert
                type="error"
                showIcon
                message="Payment Not Completed"
                description="For security reasons, expired QR codes cannot be reused."
              />

              <p style={{ color: "#888", marginTop: 12, textAlign:'start' }}>
                If payment was made, please screenshot this and contact support team regarding this transaction
              </p>
            </div>
          </div>
        )}

        {/* 🔲 QR DISPLAY */}
        {!isQrInvalid && paymentStatus !== "PAID" && qrInitData && qrImage && (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 30 }}>
            <div
              style={{
                background: "#fff",
                padding: 30,
                borderRadius: 16,
                boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
                maxWidth: 420,
                width: "100%",
                textAlign: "center",
              }}
            >
              <h3 style={{ marginBottom: 8 }}>Scan to Pay</h3>

              <p style={{ color: "#888", marginBottom: 20 }}>
                Open your banking app and scan the QR code
              </p>

              <div
                style={{
                  padding: 16,
                  border: "1px dashed #ddd",
                  borderRadius: 12,
                  marginBottom: 20,
                }}
              >
                <img
                  src={qrImage}
                  alt="VND QR Code"
                  style={{ width: "100%", maxWidth: 260 }}
                />
              </div>

              <p style={{ fontWeight: 600, marginBottom: 4 }}>
                Amount:{" "} ₫
                {new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(verifyTranData?.amount ?? 0)}{" "}
              </p>

              <p style={{ color: "#d46b08", marginBottom: 12 }}>
                QR Code Expires in: {formatTime(timeLeft)}
              </p>

              {verifyTranData?.trxRef && (
                <div
                  style={{
                    background: "#f5f7fa",
                    padding: "10px 12px",
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <b>Reference:</b> {verifyTranData.trxRef}
                </div>
              )}

            </div>
          </div>
        )}
      </MainWrapper>
    </div>
  );
};

export default VNDqrCodeInfo;
